import { useState } from "react";
import { Link } from "react-router-dom";
import config from "../config/index.js";

const FAQS = [
    {
        q: "How does a term deposit work?",
        a: "You deposit USDC into a fixed 3-year term earning 20% simple annual yield. At deposit time funds route atomically: 80% to the Safe Vault (structurally quarantined principal), 10% to a protocol-token buy, 9% to the Distribution Reserve and 1% to liquidity."
    },
    {
        q: "Is my principal structurally quarantined?",
        a: "Yes. The 80% Safe Vault slice is segregated: it cannot be used for treasury operations, liquidity, buybacks or governance withdrawals. Only the Bond contract's lifecycle flows (maturity, early exit, liquidation) can move it."
    },
    {
        q: "What happens if I exit early?",
        a: "Early exit before the 3-year maturity carries a 20% penalty of principal. You receive principal minus penalty plus accrued interest. The penalty strengthens the Distribution Reserve."
    },
    {
        q: "What is Turbo leverage?",
        a: "Turbo borrows USDC against an existing deposit's Safe Vault slice and atomically re-deposits it as a new term deposit — leveraged exposure entirely in USDC, with no external asset volatility."
    },
    {
        q: "What is Automated Reinvestment?",
        a: "Once a deposit's accrued interest crosses 100 USDC it can be reinvested automatically. Standard mode opens a fresh term deposit; Turbo mode opens a leveraged one when LTV allows. Configure it per-deposit in Settings."
    },
    {
        q: "What is the Consolidated Reinvestment Pool?",
        a: "An M4 module: attach deposits to a pool and their yield daily-compounds the 20% rate as deferred accounting. The compound-over-simple bonus materialises as a fresh term deposit on detach, manual reinvest, or liquidation."
    },
    {
        q: "Why can't I transfer my deposit NFT freely?",
        a: "Each deposit is an ERC-721C NFT with whitelist-gated, marketplace-only transfers and an enforced 5% royalty. This preserves royalty capture and compliance control. A pooled NFT is frozen until detached."
    },
    {
        q: "What happens after maturity?",
        a: "Interest stops accruing. You enter a 12-month grace period (Dormant) during which funds stay fully accessible. After that, an Abandoned deposit incurs a 5% monthly maintenance fee until withdrawn."
    }
];

/**
 * Help / Support — FAQ, a protocol-parameter reference table and key
 * contract addresses. Static, wallet-independent content.
 */
export default function Support() {
    const [open, setOpen] = useState(0);

    const params = [
        ["Term length", `${config.protocol.fixedTermDays} days (3 years)`],
        ["Annual yield", `${config.protocol.annualRateBps / 100}% simple`],
        ["Minimum deposit", `${config.protocol.minDepositUsdc} USDC`],
        ["Capital routing", "80% Vault / 10% Token / 9% Distribution Reserve / 1% Liquidity"],
        ["Early-exit penalty", `${config.protocol.earlyExitPenaltyBps / 100}% of principal`],
        ["Max borrow LTV", `${config.protocol.maxLtvBps / 100}%`],
        ["Liquidation threshold", `${config.protocol.liquidationThresholdBps / 100}%`],
        ["Loan APR", `${config.protocol.loanAprBps / 100}%`],
        ["Automated Reinvestment trigger", `${config.protocol.autoCompoundTriggerUsdc} USDC`],
        ["NFT royalty", "5% (enforced on-chain)"]
    ];

    const contracts = [
        ["Bond", config.contracts.bond],
        ["Safe Vault", config.contracts.safeVault],
        ["Distribution Reserve", config.contracts.treasury],
        ["Deposit NFT", config.contracts.depositNft],
        ["Lending", config.contracts.lending],
        ["Turbo", config.contracts.turbo],
        ["Automated Reinvestment", config.contracts.autoCompound],
        ["Reinvestment", config.contracts.reinvestment]
    ].filter(([, addr]) => addr);

    return (
        <div className="flex flex-col gap-3">

            <div className="grid gap-3 lg:grid-cols-3">
                {/* FAQ */}
                <section className="rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4 lg:col-span-2">
                    <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">
                        Frequently Asked Questions
                    </h3>
                    <ul className="mt-3 space-y-2">
                        {FAQS.map((item, i) => {
                            const expanded = open === i;
                            return (
                                <li
                                    key={item.q}
                                    className="rounded-[10px] bg-white/10 ring-1 ring-[#bfb67b]"
                                >
                                    <button
                                        type="button"
                                        onClick={() => setOpen(expanded ? -1 : i)}
                                        className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left"
                                    >
                                        <span className="font-manrope text-[12px] font-bold text-white">
                                            {item.q}
                                        </span>
                                        <span
                                            className={`shrink-0 text-[#FBC206] transition-transform ${
                                                expanded ? "rotate-45" : ""
                                            }`}
                                        >
                                            <PlusIcon />
                                        </span>
                                    </button>
                                    {expanded ? (
                                        <p className="border-t border-white/10 px-3 py-2.5 text-[11px] leading-relaxed text-[#e6c985]/80">
                                            {item.a}
                                        </p>
                                    ) : null}
                                </li>
                            );
                        })}
                    </ul>
                </section>

                {/* Parameters + contacts */}
                <section className="flex flex-col gap-3">
                    <div className="rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4">
                        <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">
                            Protocol Parameters
                        </h3>
                        <dl className="mt-2 space-y-1.5 text-[10.5px]">
                            {params.map(([label, value]) => (
                                <div
                                    key={label}
                                    className="flex items-start justify-between gap-3 border-b border-white/5 pb-1.5 last:border-0"
                                >
                                    <dt className="text-[#e6c985]/70">{label}</dt>
                                    <dd className="text-right font-semibold text-white">{value}</dd>
                                </div>
                            ))}
                        </dl>
                    </div>

                    <div className="rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4">
                        <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">
                            Need more help?
                        </h3>
                        <p className="mt-1.5 text-[10.5px] leading-snug text-[#e6c985]/75">
                            Check live solvency on the health dashboard.
                        </p>
                        <div className="mt-2.5 flex flex-col gap-1.5">
                            <Link
                                to="/protocol-health"
                                className="rounded-md bg-[#162e1f] ring-1 ring-[#bfb67b] px-3 py-1.5 text-[11px] font-semibold text-[#e6c985] hover:bg-[#1d3a27] transition-colors"
                            >
                                Protocol Health →
                            </Link>
                        </div>
                    </div>
                </section>
            </div>

            {/* Deployed contracts */}
            {contracts.length > 0 ? (
                <section className="rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4">
                    <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">
                        Deployed Contracts ({config.network.name})
                    </h3>
                    <div className="mt-2 grid gap-1.5 sm:grid-cols-2">
                        {contracts.map(([label, addr]) => (
                            <a
                                key={label}
                                href={`${config.network.blockExplorerUrl}/address/${addr}`}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center justify-between rounded-md bg-white/10 ring-1 ring-[#bfb67b] px-3 py-1.5 text-[10.5px] hover:bg-white/15 transition-colors"
                            >
                                <span className="font-semibold text-white">{label}</span>
                                <span className="text-[#FBC206]">
                                    {addr.slice(0, 8)}…{addr.slice(-6)}
                                </span>
                            </a>
                        ))}
                    </div>
                </section>
            ) : null}
        </div>
    );
}

function HelpIcon() {
    return (
        <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3" />
            <line x1="12" y1="17" x2="12" y2="17" />
        </svg>
    );
}

function PlusIcon() {
    return (
        <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true">
            <line x1="10" y1="4" x2="10" y2="16" />
            <line x1="4" y1="10" x2="16" y2="10" />
        </svg>
    );
}
